import React from 'react';
import { format, addMonths, startOfMonth } from 'date-fns';
import { Snowflake, Plus, Trash2, DollarSign, Calendar, AlertCircle, Edit2, X, CheckCircle, XCircle, Info, FileDown } from 'lucide-react';
import { collection, addDoc, onSnapshot, Timestamp, deleteDoc, doc, query, where, getDocs, writeBatch, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { addEvent } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import useEvents from '../hooks/useEvents';
import DeleteUserDialog from './DeleteUserDialog';
import FirebaseTest from './FirebaseTest';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface Debt {
  id: string;
  userId: string;
  name: string;
  balance: number;
  interestRate: number;
  minimumPayment: number;
  dueDate: number;
  createdAt: Date;
  paidOff?: boolean;
  paidOffDate?: Date;
  snowballedAmount?: number;
  totalPaid?: number;
  previousPayment?: number;
}

export default function DebtSnowball() {
  const [showForm, setShowForm] = React.useState(false);
  const [debts, setDebts] = React.useState<Debt[]>([]);
  const { user } = useAuth();
  const { events } = useEvents();
  const [editingDebt, setEditingDebt] = React.useState<Debt | null>(null);
  const [formData, setFormData] = React.useState({
    name: '',
    balance: '',
    interestRate: '',
    minimumPayment: '',
    dueDate: '1'
  });

  React.useEffect(() => {
    if (!user) return;

    const debtsQuery = query(
      collection(db, 'debts'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(debtsQuery, (snapshot) => {
      const debtsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        paidOffDate: doc.data().paidOffDate?.toDate()
      })) as Debt[];
      
      setDebts(debtsList.sort((a, b) => {
        if (a.paidOff && b.paidOff) {
          return (b.paidOffDate?.getTime() || 0) - (a.paidOffDate?.getTime() || 0);
        }
        if (a.paidOff || a.balance === 0) return 1;
        if (b.paidOff || b.balance === 0) return -1;
        return (a.balance || 0) - (b.balance || 0);
      }));
    });

    return () => unsubscribe();
  }, [user]);

  const createMonthlyPayments = async (debt: Debt, docRef: any) => {
    const today = new Date();
    const startDate = startOfMonth(today);
    const paymentDay = new Date(startDate.getFullYear(), startDate.getMonth(), debt.dueDate);
    const recurringGroupId = crypto.randomUUID();

    // Only create one payment for the current month
    await addEvent({
      id: crypto.randomUUID(),
      title: `${debt.name} Payment`,
      amount: debt.minimumPayment,
      type: 'bill',
      date: paymentDay,
      category: 'debt',
      isRecurring: true,
      frequency: 'monthly',
      debtId: docRef.id,
      recurringGroupId
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const debtData = {
        userId: user.uid,
        name: formData.name.trim(),
        balance: parseFloat(formData.balance),
        interestRate: parseFloat(formData.interestRate),
        minimumPayment: parseFloat(formData.minimumPayment),
        dueDate: parseInt(formData.dueDate),
        createdAt: Timestamp.now(),
        totalPaid: 0
      };

      if (editingDebt) {
        const debtRef = doc(db, 'debts', editingDebt.id);
        await updateDoc(debtRef, {
          ...debtData,
          updatedAt: Timestamp.now()
        });

        const eventsRef = collection(db, 'events');
        const futureEvents = await getDocs(
          query(eventsRef, 
            where('debtId', '==', editingDebt.id),
            where('date', '>=', new Date())
          )
        );

        const batch = writeBatch(db);
        futureEvents.docs.forEach(doc => {
          batch.update(doc.ref, {
            title: `${debtData.name} Payment`,
            amount: debtData.minimumPayment,
            updatedAt: Timestamp.now()
          });
        });

        await batch.commit();
      } else {
        const docRef = await addDoc(collection(db, 'debts'), debtData);
        await createMonthlyPayments(debtData, docRef);
      }

      setFormData({
        name: '',
        balance: '',
        interestRate: '',
        minimumPayment: '',
        dueDate: '1'
      });
      setShowForm(false);
      setEditingDebt(null);
    } catch (error) {
      console.error('Error saving debt:', error);
      alert('Failed to save debt. Please try again.');
    }
  };

  const handleEditDebt = (debt: Debt) => {
    setEditingDebt(debt);
    setFormData({
      name: debt.name,
      balance: debt.balance.toString(),
      interestRate: debt.interestRate.toString(),
      minimumPayment: debt.minimumPayment.toString(),
      dueDate: debt.dueDate.toString()
    });
    setShowForm(true);
  };

  const handleDeleteDebt = async (id: string) => {
    if (!user) return;

    try {
      await deleteDoc(doc(db, 'debts', id));
      
      const eventsRef = collection(db, 'events');
      const snapshot = await getDocs(query(eventsRef, where('debtId', '==', id)));
      
      const batch = writeBatch(db);
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    } catch (error) {
      console.error('Error deleting debt:', error);
      alert('Failed to delete debt. Please try again.');
    }
  };

  const handleMarkPaidOff = async (debt: Debt) => {
    if (!user) return;

    try {
      const debtRef = doc(db, 'debts', debt.id);
      await updateDoc(debtRef, {
        paidOff: true,
        paidOffDate: Timestamp.now(),
        updatedAt: Timestamp.now(),
        previousPayment: debt.minimumPayment
      });

      const nextDebt = debts.find(d => 
        !d.paidOff && 
        d.id !== debt.id && 
        (d.balance || 0) > 0
      );

      if (nextDebt) {
        const newPayment = (nextDebt.minimumPayment || 0) + (debt.minimumPayment || 0);
        
        const nextDebtRef = doc(db, 'debts', nextDebt.id);
        await updateDoc(nextDebtRef, {
          minimumPayment: newPayment,
          snowballedAmount: (nextDebt.snowballedAmount || 0) + (debt.minimumPayment || 0),
          updatedAt: Timestamp.now()
        });

        const eventsRef = collection(db, 'events');
        const futureEvents = await getDocs(
          query(eventsRef, 
            where('debtId', '==', nextDebt.id),
            where('date', '>=', new Date())
          )
        );

        const batch = writeBatch(db);
        futureEvents.docs.forEach(doc => {
          batch.update(doc.ref, {
            amount: newPayment,
            title: `${nextDebt.name} Payment (Snowballed +$${(debt.minimumPayment || 0).toFixed(2)})`,
            updatedAt: Timestamp.now()
          });
        });

        await batch.commit();
      }

      const eventsRef = collection(db, 'events');
      const futureEvents = await getDocs(
        query(eventsRef, 
          where('debtId', '==', debt.id),
          where('date', '>=', new Date())
        )
      );

      const batch = writeBatch(db);
      futureEvents.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking debt as paid off:', error);
      alert('Failed to mark debt as paid off. Please try again.');
    }
  };

  const handleUnmarkPaidOff = async (debt: Debt) => {
    if (!user) return;

    try {
      const snowballedDebt = debts.find(d => 
        !d.paidOff && 
        d.snowballedAmount && 
        d.snowballedAmount > 0
      );

      if (snowballedDebt) {
        const originalPayment = snowballedDebt.minimumPayment - (debt.minimumPayment || 0);
        
        const snowballedDebtRef = doc(db, 'debts', snowballedDebt.id);
        await updateDoc(snowballedDebtRef, {
          minimumPayment: originalPayment,
          snowballedAmount: (snowballedDebt.snowballedAmount || 0) - (debt.minimumPayment || 0),
          updatedAt: Timestamp.now()
        });

        const snowballedEventsRef = collection(db, 'events');
        const snowballedFutureEvents = await getDocs(
          query(snowballedEventsRef,
            where('debtId', '==', snowballedDebt.id),
            where('date', '>=', new Date())
          )
        );

        const snowballedBatch = writeBatch(db);
        snowballedFutureEvents.docs.forEach(doc => {
          snowballedBatch.update(doc.ref, {
            amount: originalPayment,
            title: snowballedDebt.name + ' Payment',
            updatedAt: Timestamp.now()
          });
        });

        await snowballedBatch.commit();
      }

      const debtRef = doc(db, 'debts', debt.id);
      await updateDoc(debtRef, {
        paidOff: false,
        paidOffDate: null,
        minimumPayment: debt.previousPayment || debt.minimumPayment,
        updatedAt: Timestamp.now()
      });

      await createMonthlyPayments(debt, { id: debt.id });

    } catch (error) {
      console.error('Error unmarking debt as paid off:', error);
      alert('Failed to unmark debt as paid off. Please try again.');
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text('Debt Snowball Report', 14, 20);
    doc.setFontSize(12);
    doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy')}`, 14, 30);

    // Summary section
    const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);
    const totalMonthlyPayments = debts.reduce((sum, debt) => sum + debt.minimumPayment, 0);
    const totalPaid = debts.reduce((sum, debt) => sum + (debt.totalPaid || 0), 0);
    
    doc.setFontSize(14);
    doc.text('Summary', 14, 45);
    doc.setFontSize(10);
    doc.text(`Total Debt: $${totalDebt.toFixed(2)}`, 14, 55);
    doc.text(`Total Monthly Payments: $${totalMonthlyPayments.toFixed(2)}`, 14, 62);
    doc.text(`Total Paid: $${totalPaid.toFixed(2)}`, 14, 69);

    // Active Debts Table
    const activeDebts = debts.filter(debt => !debt.paidOff);
    if (activeDebts.length > 0) {
      doc.setFontSize(14);
      doc.text('Active Debts', 14, 85);
      
      doc.autoTable({
        startY: 90,
        head: [['Debt Name', 'Balance', 'Interest Rate', 'Monthly Payment', 'Total Paid', 'Due Date']],
        body: activeDebts.map(debt => [
          debt.name,
          `$${debt.balance.toFixed(2)}`,
          `${debt.interestRate}%`,
          `$${debt.minimumPayment.toFixed(2)}`,
          `$${(debt.totalPaid || 0).toFixed(2)}`,
          `Day ${debt.dueDate}`
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [88, 80, 236] },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 30, halign: 'right' },
          2: { cellWidth: 25, halign: 'right' },
          3: { cellWidth: 30, halign: 'right' },
          4: { cellWidth: 30, halign: 'right' },
          5: { cellWidth: 25, halign: 'center' }
        }
      });
    }

    // Paid Off Debts Table
    const paidOffDebts = debts.filter(debt => debt.paidOff);
    if (paidOffDebts.length > 0) {
      const finalY = (doc as any).lastAutoTable.finalY || 120;
      doc.setFontSize(14);
      doc.text('Paid Off Debts', 14, finalY + 15);
      
      doc.autoTable({
        startY: finalY + 20,
        head: [['Debt Name', 'Original Balance', 'Total Paid', 'Paid Off Date']],
        body: paidOffDebts.map(debt => [
          debt.name,
          `$${debt.balance.toFixed(2)}`,
          `$${(debt.totalPaid || 0).toFixed(2)}`,
          debt.paidOffDate ? format(debt.paidOffDate, 'MMM d, yyyy') : 'N/A'
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [34, 197, 94] },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 40, halign: 'right' },
          2: { cellWidth: 40, halign: 'right' },
          3: { cellWidth: 40, halign: 'center' }
        }
      });
    }

    // Save the PDF
    doc.save(`debt_snowball_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Snowflake className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Authentication Required</h3>
          <p className="mt-1 text-sm text-gray-500">Please sign in to access the debt snowball.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Balance Update Note */}
        <div className="mb-6 glass-effect border-l-4 border-blue-500 p-4 rounded-lg animate-fadeIn">
          <div className="flex">
            <Info className="h-6 w-6 text-blue-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Important Note</h3>
              <p className="mt-1 text-sm text-blue-700">
                Please remember to periodically update your debt balances manually. ClearCash does not directly connect to any banks or financial institutions, so keeping your balances up to date ensures accurate tracking and snowball calculations.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center">
            <Snowflake className="w-8 h-8 mr-3 text-blue-600" />
            Debt Snowball
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={handleExportPDF}
              className="flex items-center px-4 py-2 glass-effect text-slate-700 rounded-lg hover:scale-105 transition-all duration-300 mr-2"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </button>
            <button
              onClick={() => {
                setEditingDebt(null);
                setFormData({
                  name: '',
                  balance: '',
                  interestRate: '',
                  minimumPayment: '',
                  dueDate: '1'
                });
                setShowForm(!showForm);
              }}
              className="flex items-center px-4 py-2 gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg cyber-glow"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Debt
            </button>
          </div>
        </div>

        {showForm && (
          <div className="mb-8 bg-white p-6 rounded-lg shadow-lg border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Debt Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Balance</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.balance}
                      onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                      className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.interestRate}
                    onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Monthly Payment</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.minimumPayment}
                      onChange={(e) => setFormData({ ...formData, minimumPayment: e.target.value })}
                      className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <select
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingDebt(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  {editingDebt ? 'Update Debt' : 'Add Debt'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-6">
          {debts.map((debt) => (
            <div
              key={debt.id}
              className={`glass-effect rounded-xl overflow-hidden hover:scale-105 transition-all duration-300 animate-fadeIn ${
                debt.paidOff ? 'opacity-75' : 'cyber-glow'
              }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-bold text-lg bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">{debt.name}</h3>
                      {debt.paidOff && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gradient-success text-white">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Paid Off
                        </span>
                      )}
                      {debt.snowballedAmount && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gradient-primary text-white">
                          +${debt.snowballedAmount.toFixed(2)} Snowballed
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">Due on day {debt.dueDate}</p>
                  </div>
                  <div className="flex space-x-2">
                    {!debt.paidOff ? (
                      <>
                        <button
                          onClick={() => handleEditDebt(debt)}
                          className="p-2 glass-effect rounded-lg hover:scale-110 transition-all duration-300 text-blue-600"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleMarkPaidOff(debt)}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg gradient-success text-white hover:scale-105 transition-all duration-300 shadow-lg"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Paid Off
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleUnmarkPaidOff(debt)}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Unmark Paid Off
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteDebt(debt.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">Balance</div>
                    <div className="text-lg font-semibold text-gray-900">
                      ${debt.balance.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Interest Rate</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {debt.interestRate}%
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Monthly Payment</div>
                    <div className="text-lg font-semibold text-gray-900">
                      ${debt.minimumPayment.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Total Paid</div>
                    <div className="text-lg font-semibold text-green-600">
                      ${(debt.totalPaid || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {debts.length === 0 && !showForm && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Snowflake className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No debts</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first debt.</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Debt
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}